import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { stripePromise } from "@/lib/stripe";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { StripeElementsOptions } from "@stripe/stripe-js";
import { CreditCard, Loader2 } from "lucide-react";
import { useState } from "react";

interface PaymentFormProps {
  // The Stripe SetupIntent client secret from the subscribe
  // workflow. Used during confirmation, not for Elements init.
  clientSecret?: string;

  // Whether this org is eligible for a free trial.
  trialEligible?: boolean;

  // Called after the card is successfully confirmed.
  // `setupIntentId` is the confirmed SetupIntent ID.
  onSuccess: (setupIntentId: string) => void;

  // Called when confirmation fails.
  onError: (message: string) => void;

  // Label for the submit button.
  submitLabel?: string;

  // Whether the parent form is submitting (disables the form).
  isSubmitting?: boolean;

  // Optional prefix for the card title (e.g. "Step 2: ").
  titlePrefix?: string;
}

// Appearance API configuration to match the shadcn/ui theme.
// HSL values sourced from index.css :root variables.
const appearance = {
  theme: "flat" as const,
  variables: {
    colorPrimary: "hsl(211, 72%, 22%)",
    colorBackground: "hsl(0, 0%, 100%)",
    colorText: "hsl(240, 10%, 3.9%)",
    colorTextSecondary: "hsl(240, 3.8%, 46.1%)",
    colorDanger: "hsl(0, 84.2%, 60.2%)",
    borderRadius: "0.5rem",
    fontFamily:
      "ui-sans-serif, system-ui, sans-serif, " +
      '"Apple Color Emoji", "Segoe UI Emoji", ' +
      '"Segoe UI Symbol", "Noto Color Emoji"',
    fontSizeBase: "14px",
    spacingUnit: "4px",
  },
  rules: {
    ".Input": {
      border: "1px solid hsl(240, 5.9%, 90%)",
      boxShadow: "none",
      padding: "0.5rem 0.75rem",
      fontSize: "14px",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    },
    ".Input:focus": {
      border: "1px solid hsl(211, 72%, 22%)",
      boxShadow: "0 0 0 2px hsla(115, 49%, 76%, 0.3)",
    },
    ".Label": {
      fontSize: "14px",
      fontWeight: "500",
      marginBottom: "6px",
    },
    ".Error": {
      fontSize: "13px",
      color: "hsl(0, 84.2%, 60.2%)",
    },
  },
};

// Inner form component that uses Stripe hooks (must be inside
// <Elements>).
function PaymentFormInner({
  onSuccess,
  onError,
  submitLabel = "Save card",
  isSubmitting = false,
  clientSecret,
  trialEligible = false,
  titlePrefix = "",
}: Omit<PaymentFormProps, never> & {
  clientSecret?: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isConfirming, setIsConfirming] = useState(false);
  const [cardError, setCardError] = useState("");
  const [paymentComplete, setPaymentComplete] = useState(false);

  const disabled =
    !stripe ||
    !elements ||
    isSubmitting ||
    isConfirming ||
    !clientSecret ||
    !paymentComplete;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setCardError("");
    setIsConfirming(true);

    // Validate the payment element first.
    const { error: submitError } = await elements.submit();

    if (submitError) {
      setIsConfirming(false);
      setCardError(submitError.message || "Please check your card details.");
      onError(submitError.message || "Please check your card details.");
      return;
    }

    // Confirm the SetupIntent with the client secret from the
    // subscribe workflow.
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      clientSecret,
      redirect: "if_required",
      confirmParams: {
        return_url: window.location.href,
      },
    });

    setIsConfirming(false);

    if (error) {
      setCardError(error.message || "Payment confirmation failed.");
      onError(error.message || "Payment confirmation failed.");
      return;
    }

    if (setupIntent) {
      onSuccess(setupIntent.id);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <Card>
          {trialEligible && (
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {titlePrefix}Payment method
                <CreditCard className="h-5 w-5 text-primary" />
              </CardTitle>
              <CardDescription>
                New customers get a free starter app for one month. After that,{" "}
                <strong className="font-semibold">
                  you're only charged for the applications you run
                </strong>
                , so you won't be charged today. See the{" "}
                <a
                  href="https://reboot.dev/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline"
                >
                  pricing page
                </a>{" "}
                for details.
              </CardDescription>
            </CardHeader>
          )}
          <CardContent>
            <PaymentElement
              options={{
                layout: "tabs",
              }}
              onChange={(e) => setPaymentComplete(e.complete)}
            />
            {cardError && (
              <p className="text-sm text-destructive mt-2">{cardError}</p>
            )}
          </CardContent>
        </Card>
        <Button type="submit" size="lg" className="w-full" disabled={disabled}>
          {isConfirming || isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {isConfirming ? "Confirming..." : "Saving..."}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
}

export function PaymentForm({
  clientSecret,
  trialEligible,
  onSuccess,
  onError,
  submitLabel,
  isSubmitting,
  titlePrefix,
}: PaymentFormProps) {
  // Always use deferred mode for Elements initialization.
  // The `clientSecret` is used at confirmation time, not for
  // initializing Elements.
  const options: StripeElementsOptions = {
    mode: "setup" as const,
    currency: "usd",
    appearance,
    paymentMethodTypes: ["card"],
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentFormInner
        clientSecret={clientSecret}
        trialEligible={trialEligible}
        onSuccess={onSuccess}
        onError={onError}
        submitLabel={submitLabel}
        isSubmitting={isSubmitting}
        titlePrefix={titlePrefix}
      />
    </Elements>
  );
}
